import { useState, useEffect } from 'react';
import { useTravelNews } from '@/hooks/useTravelNews';
import { NewsGrid } from '@/components/news/NewsGrid';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Search, RefreshCw, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

export default function TravelNewsPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('');
  const [isAutoFetching, setIsAutoFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const { toast } = useToast();
  
  // Pagination config - 12 items per page with max 3 pages
  const { news, isLoading, error, refetch, totalPages, totalCount } = useTravelNews({
    limit: 12,
    page: currentPage,
    filter,
    maxPages: 3 // Limit to 3 pages
  });

  // Auto-fetch news if none exists
  useEffect(() => {
    const checkAndFetchNews = async () => {
      // Only check if we have no news and not currently auto-fetching
      if (news.length === 0 && !isLoading && !isAutoFetching) {
        setIsAutoFetching(true);
        setFetchError(null);
        
        try {
          // Check if we have any news at all in the database
          const { count, error: countError } = await supabase
            .from('travel_news')
            .select('id', { count: 'exact' });
            
          if (countError) {
            throw countError;
          }
          
          // If no news in database, trigger the fetch
          if (count === 0) {
            toast({
              title: "Auto-fetching news",
              description: "No travel news found. Fetching latest updates...",
            });
            
            await fetchNews();
          }
        } catch (err: any) {
          console.error('Error checking/fetching news:', err);
          setFetchError(`Failed to check or fetch news: ${err.message}`);
          toast({
            title: "Error",
            description: "Failed to check or fetch news. Please try again later.",
            variant: "destructive"
          });
        } finally {
          setIsAutoFetching(false);
        }
      }
    };
    
    checkAndFetchNews();
  }, [news.length, isLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilter(searchQuery);
    setCurrentPage(1);
  };

  const fetchNews = async (retryCount = 0) => {
    setIsAutoFetching(true);
    setFetchError(null);
    setFetchAttempt(prev => prev + 1);
    setDebugInfo(null);
    
    try {
      console.log('Invoking fetch-travel-news function, attempt:', retryCount + 1);
      
      // Add a timeout to the function invocation
      const fetchWithTimeout = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
        
        try {
          // Call the edge function
          const { data, error } = await supabase.functions.invoke('fetch-travel-news', {
            body: { 
              force: true
            }
          });
          
          clearTimeout(timeoutId);
          return { data, error };
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      };
      
      const response = await fetchWithTimeout();
      const { data, error } = response;

      // Save debug info
      setDebugInfo(response);
      console.log('Full response from edge function:', response);

      if (error) {
        console.error('Error fetching news:', error);
        
        // If this is the first attempt and it failed, try once more
        if (retryCount === 0) {
          setIsRetrying(true);
          toast({
            title: "Retrying...",
            description: "First attempt failed, trying again.",
          });
          
          // Wait a moment before retrying
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          setIsRetrying(false);
          return await fetchNews(retryCount + 1);
        }
        
        setFetchError(`Failed to fetch news: ${error.message || 'Failed to send a request to the Edge Function'}`);
        toast({
          title: "Error",
          description: `Failed to fetch news: ${error.message || 'Unknown error'}`,
          variant: "destructive"
        });
        return;
      }

      console.log('Fetch news response:', data);
      
      // Refetch to display the newly fetched news
      await refetch();
      
      toast({
        title: "Success",
        description: data.message || `Successfully processed ${data.count} news articles`,
      });
    } catch (error: any) {
      console.error('Error invoking function:', error);
      setFetchError(`Failed to fetch news: ${error.message || 'An unexpected error occurred'}`);
      setDebugInfo(error);
      toast({
        title: "Error",
        description: `Failed to send request to Edge Function. This could be due to network issues or a timeout.`,
        variant: "destructive"
      });
    } finally {
      setIsAutoFetching(false);
    }
  };

  const handleRefresh = async () => {
    await fetchNews();
  };

  return (
    <main className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <section className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Travel News & Updates</h1>
          <p className="text-muted-foreground">
            Stay informed with the latest travel news, trends, and destination updates from around the world.
          </p>
        </section>

        <section className="mb-8">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search news articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  aria-label="Search news articles"
                />
              </div>
              <Button type="submit">Search</Button>
            </form>
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={isLoading || isAutoFetching}
              className="flex items-center gap-2"
              aria-label="Refresh news"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading || isAutoFetching ? 'animate-spin' : ''}`} />
              {isAutoFetching ? (isRetrying ? 'Retrying...' : 'Fetching...') : 'Refresh'}
            </Button>
          </div>
        </section>

        {error && (
          <Alert className="mb-8" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Error loading travel news: {error.message}
            </AlertDescription>
          </Alert>
        )}

        {fetchError && (
          <Alert className="mb-8" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to fetch news</AlertTitle>
            <AlertDescription>
              <p>{fetchError}</p>
              <p className="mt-2">
                This could be due to API rate limits or network issues. Please try again later.
              </p>
              {fetchAttempt > 0 && (
                <p className="mt-1">
                  Attempt: {fetchAttempt} - Function might be timing out while processing the request.
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {debugInfo && debugInfo.data && debugInfo.data.searchQuery && (
          <Alert className="mb-8">
            <AlertTitle>Search Information</AlertTitle>
            <AlertDescription>
              <p>Search query used: "{debugInfo.data.searchQuery}"</p>
              {debugInfo.data.count !== undefined && (
                <p>Found and inserted {debugInfo.data.count} travel-related articles</p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {news.length === 0 && !isLoading && !isAutoFetching && !fetchError && (
          <Alert className="mb-8">
            <AlertTitle>No news found</AlertTitle>
            <AlertDescription>
              Click the "Refresh" button to fetch the latest news.
            </AlertDescription>
          </Alert>
        )}

        {isAutoFetching && (
          <Alert className="mb-8">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            <AlertTitle>{isRetrying ? "Retrying news fetch..." : "Fetching travel news..."}</AlertTitle>
            <AlertDescription>
              This may take a moment.
            </AlertDescription>
          </Alert>
        )}

        <NewsGrid news={news} isLoading={isLoading} />

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination className="mt-8">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                  aria-disabled={currentPage === 1}
                />
              </PaginationItem>
              
              {Array.from({ length: totalPages }).map((_, i) => (
                <PaginationItem key={i} className="hidden sm:inline-block">
                  <PaginationLink
                    onClick={() => setCurrentPage(i + 1)}
                    isActive={currentPage === i + 1}
                    aria-current={currentPage === i + 1 ? 'page' : undefined}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                  aria-disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </main>
  );
}
